import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, EyeIcon, EyeOffIcon, LoaderCircleIcon, LockKeyholeIcon, MailIcon, ShieldCheckIcon } from 'lucide-react';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { AdminService } from '../../lib/supabase/services/admin.service';
import { AdminBrand } from './AdminBrand';
import { AdminStatusScreen } from './AdminStatusScreen';
export function AdminLoginPage() {
  const {
    status,
    message
  } = useAdminAccess();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  useEffect(() => {
    if (status === 'authorized') {
      window.location.replace('/admin');
    }
  }, [status]);
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;
    setFormError(null);
    setIsSubmitting(true);
    try {
      await AdminService.signIn(email.trim(), password);
    } catch {
      setFormError('No fue posible iniciar sesión. Verificá tus credenciales e intentá nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  }
  if (status === 'configuration-error') {
    return <AdminStatusScreen title="Configuración requerida" message={message} tone="warning" data-id="element-137" />;
  }
  if (status === 'loading') {
    return <AdminStatusScreen title="Verificando acceso" message="Comprobando tu sesión segura." loading data-id="element-138" />;
  }
  if (status === 'inactive') {
    return <AdminStatusScreen title="Usuario desactivado" message="Esta cuenta no tiene acceso activo a TITÁN OS." tone="warning" data-id="element-139">
        <a href="/admin/login" className="mt-7 inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-cyan-300/30 bg-cyan-300/10 px-4 text-sm font-semibold text-cyan-100 transition-colors hover:bg-cyan-300/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" data-id="element-140">
          Volver al inicio de sesión
        </a>
      </AdminStatusScreen>;
  }
  if (status === 'profile-missing' || status === 'error') {
    return <AdminStatusScreen title="Acceso no disponible" message={message} tone="warning" data-id="element-141" />;
  }
  return <main className="flex min-h-screen w-full items-center justify-center bg-[#07111f] px-4 py-8 text-slate-100" data-id="element-142">
      <section className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0b1728] p-6 shadow-2xl shadow-black/30 sm:p-8" data-id="element-143">
        <AdminBrand data-id="element-144" />
        <div className="mt-9" data-id="element-145">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-300/10 text-cyan-200" data-id="element-146">
            <ShieldCheckIcon className="h-5 w-5" data-id="element-147" />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-white" data-id="element-148">
            Ingresá a TITÁN OS
          </h1>
          <p className="mt-2 text-sm leading-6 text-slate-300" data-id="element-149">
            Usá tus credenciales autorizadas para acceder al entorno
            administrativo.
          </p>
        </div>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit} data-id="element-150">
          <div data-id="element-151">
            <label htmlFor="admin-email" className="mb-2 block text-sm font-medium text-slate-200" data-id="element-152">
              Correo electrónico
            </label>
            <div className="relative" data-id="element-153">
              <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" data-id="element-154" />
              <input id="admin-email" type="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} required disabled={isSubmitting} className="min-h-12 w-full rounded-xl border border-white/10 bg-[#07111f] py-3 pl-10 pr-4 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20 disabled:cursor-not-allowed disabled:opacity-60" placeholder="nombre@empresa.com" data-id="element-155" />
            </div>
          </div>

          <div data-id="element-156">
            <label htmlFor="admin-password" className="mb-2 block text-sm font-medium text-slate-200" data-id="element-157">
              Contraseña
            </label>
            <div className="relative" data-id="element-158">
              <LockKeyholeIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" data-id="element-159" />
              <input id="admin-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required disabled={isSubmitting} className="min-h-12 w-full rounded-xl border border-white/10 bg-[#07111f] py-3 pl-10 pr-12 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20 disabled:cursor-not-allowed disabled:opacity-60" placeholder="Tu contraseña" data-id="element-160" />
              <button type="button" onClick={() => setShowPassword(current => !current)} className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} data-id="element-161">
                {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-162" /> : <EyeIcon className="h-4 w-4" data-id="element-163" />}
              </button>
            </div>
          </div>

          {formError && <p role="alert" className="flex gap-2 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm leading-5 text-red-100" data-id="element-164">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-none" data-id="element-165" />
              {formError}
            </p>}

          <button type="submit" disabled={isSubmitting} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-cyan-300 px-4 text-sm font-bold text-[#06202a] transition-colors hover:bg-cyan-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1728] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-166">
            {isSubmitting ? <>
                <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-167" />
                Verificando…
              </> : 'Ingresar de forma segura'}
          </button>
        </form>
      </section>
    </main>;
}